import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  ArrowRight,
  Bookmark,
  Check,
  ChevronDown,
  ChevronUp,
  CircleUserRound,
  Copy,
  FileText,
  HelpCircle,
  History,
  ImagePlus,
  Linkedin,
  MessageCircleMore,
  Plus,
  RefreshCw,
  Shuffle,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  UsersRound,
  WandSparkles,
  Zap,
} from 'lucide-react';
import './VibeV3.css';
import VibeStoryTour from './VibeStoryTour';

const LOCAL_API_ORIGIN = ['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'https://vibes.terencela.com' : '';
const API = `${process.env.REACT_APP_BACKEND_URL || LOCAL_API_ORIGIN}/api`;
const EMAIL_KEY = 'vibeEmailV1';
const PROFILE_KEY = 'vibeV3ProfilesV1';
const PROFILE_SEED_KEY = 'vibeV3StarterRosterV1';
const HISTORY_KEY = 'vibeV3HistoryV1';
const SAVED_KEY = 'vibeV3SavedV1';
const TOUR_KEY = 'vibeStoryTourSeenV3';

const RELATIONSHIPS = [
  ['dating', 'Dating'], ['partner', 'Partner'], ['ex', 'Ex'], ['friend', 'Friend'],
  ['family', 'Family'], ['boss', 'Boss'], ['coworker', 'Coworker'], ['employee', 'Employee'],
  ['client', 'Client'], ['investor', 'Investor'], ['roommate', 'Roommate'], ['custom', 'Custom'],
];

const PLAY_CATEGORIES = [
  ['corporate', 'Corporate', '💼'],
  ['internet', 'Internet', '🔥'],
  ['celebrity', 'Characters', '🎤'],
  ['tone', 'Tone', '💬'],
  ['generation', 'Generations', '👴'],
  ['culture', 'Culture', '🌍'],
];

const PLAY_MODES = [
  ['linkedin_cringe', 'LinkedIn Cringe', 'Turn any moment into a performative leadership lesson.', '🤝', 'corporate'],
  ['finance_bro', 'Finance Bro', 'Make it sound aggressively Wall Street.', '💰', 'corporate'],
  ['tech_bro', 'Tech Bro', 'Add AI-native Silicon Valley energy.', '🧠', 'corporate'],
  ['elon_musk', 'Elon Musk Mode', 'Turn a mundane problem into a first-principles mission.', '🚀', 'celebrity'],
  ['plain_to_corporate', 'Corporate Speak', 'Make a sentence meeting-ready and politely evasive.', '💼', 'corporate'],
  ['founder_mode', 'Founder Mode', 'Turn a normal thought into a category-defining company.', '🚀', 'corporate'],
  ['startup_decoder', 'Startup → Reality', 'Decode the pitch deck into what is actually happening.', '📉', 'corporate'],
  ['corporate_decoder', 'Corporate → Reality', 'Translate workplace language into the actual message.', '🗂️', 'corporate'],
  ['marketing_decoder', 'Marketing → Reality', 'Strip the campaign language down to the claim.', '📣', 'corporate'],
  ['investor_decoder', 'VC → Reality', 'Decode investor language and soft passes.', '🔍', 'corporate'],
  ['investor_speak', 'VC Speak', 'Turn a thought into portfolio-company language.', '📈', 'corporate'],
  ['mbb_consultant', 'MBB Consultant', 'Find the so-what without boiling the ocean.', '📊', 'corporate'],
  ['plain_to_marketing', 'Marketing Fluff', 'Add maximum positioning and minimum specificity.', '✨', 'corporate'],
  ['casual_to_formal', 'Casual → Formal', 'Make a casual message professionally polished.', '🧾', 'corporate'],
  ['ai_to_vc', 'Idea → Investor Pitch', 'Package an idea for a venture conversation.', '💸', 'corporate'],

  ['wall_street_bets', 'Wall Street Bets', 'Add maximum risk and diamond hands.', '💎', 'internet'],
  ['plain_to_therapy', 'Therapy Speak', 'Turn it into boundary-and-healing language.', '🫶', 'internet'],
  ['normal_to_rizz', 'Add Rizz', 'Give it one playful, sendable escalation.', '💘', 'internet'],
  ['professional_to_meme', 'Meme Speak', 'Translate professional language into internet brain.', '🫠', 'internet'],
  ['human_to_alpha', 'Alpha Male', 'Add absurd dominance-podcast energy.', '🐺', 'internet'],
  ['sober_to_drunk', 'Drunk Text', 'Make it 1:17am honest—but still readable.', '🍸', 'internet'],
  ['male_to_female', 'Direct → Contextual', 'Add emotional and situational context.', '↗️', 'internet'],
  ['male_to_meaning', 'They Said → Meant', 'Decode a direct-looking message for subtext.', '🧩', 'internet'],
  ['female_to_male', 'Context → Meaning', 'Compress contextual language into the likely point.', '➡️', 'internet'],
  ['therapy_decoder', 'Therapy → Plain', 'Decode polished therapeutic language.', '🛋️', 'internet'],
  ['msn_chat', '90s MSN Chat', 'Bring back away-message internet energy.', '💻', 'internet'],

  ['trump_decoder', 'Trump → Reality', 'Decode the superlatives into the underlying claim.', '🗞️', 'celebrity'],
  ['trump_speak', 'Trump Mode', 'Everything becomes either tremendous or a disaster.', '🇺🇸', 'celebrity'],
  ['david_attenborough', 'David Attenborough', 'Narrate the mundane like rare wildlife.', '🦉', 'celebrity'],
  ['michael_scott', 'Michael Scott', 'Make it an awkward office announcement.', '📎', 'celebrity'],
  ['patrick_bateman', 'Patrick Bateman', 'Turn one tiny detail into a status emergency.', '🪪', 'celebrity'],
  ['walter_white', 'Walter White', 'Make a small inconvenience dangerously methodical.', '⚗️', 'celebrity'],
  ['saul_goodman', 'Saul Goodman', 'Find the loophole. Sell the loophole.', '⚖️', 'celebrity'],
  ['homelander', 'Homelander', 'Make a tiny win feel weirdly authoritarian.', '🦅', 'celebrity'],
  ['shrek', 'Shrek', 'Apply uncompromising swamp logic.', '🧅', 'celebrity'],
  ['spongebob', 'SpongeBob', 'Celebrate the smallest thing at maximum volume.', '🍍', 'celebrity'],
  ['gollum', 'Gollum', 'Make the real problem precious and suspicious.', '💍', 'celebrity'],
  ['darth_vader', 'Darth Vader', 'Escalate it into an imperial command failure.', '🌑', 'celebrity'],
  ['ai_doomer', 'AI Doomer', 'Receive the bleakest possible AI prediction.', '☄️', 'celebrity'],
  ['techno_optimist', 'Techno-Optimist', 'Receive a maximalist technology response.', '🌐', 'celebrity'],
  ['hormozi_mode', 'Hormozi Reply', 'Get a brutally direct next action.', '🧱', 'celebrity'],
  ['gordon_ramsay', 'Gordon Ramsay', 'Receive a kitchen-grade reaction.', '🔥', 'celebrity'],
  ['joe_rogan', 'Joe Rogan', 'Turn it into a curious podcast detour.', '🎧', 'celebrity'],
  ['valley_girl', 'Valley Girl', 'Make the reaction dramatically conversational.', '💅', 'celebrity'],
  ['reddit_aita', 'Reddit AITA', 'Judge the situation like a comment section.', '⚖️', 'celebrity'],
  ['asian_parent', 'Asian Parent', 'Receive a strict achievement-focused reaction.', '📚', 'celebrity'],

  ['anxious_to_avoidant', 'Anxious → Avoidant', 'Rewrite pursuit as emotional distance.', '🏃', 'tone'],
  ['avoidant_to_anxious', 'Avoidant → Anxious', 'Rewrite distance as reassurance-seeking.', '🫂', 'tone'],
  ['angry_to_calm', 'Angry → Calm', 'Keep the point and remove the heat.', '🧊', 'tone'],
  ['mean_to_nice', 'Mean → Nice', 'Make the message kind without losing meaning.', '🌷', 'tone'],
  ['serious_to_sarcastic', 'Serious → Sarcastic', 'Add a dry, sharp comedic edge.', '🙃', 'tone'],
  ['passive_to_direct', 'Passive → Direct', 'Replace hints with a clear ask.', '🎯', 'tone'],
  ['rough_to_polite', 'Rough → Polite', 'Clean up blunt language without making it weak.', '🤝', 'tone'],

  ['genz_to_boomer', 'Gen Z → Boomer', 'Translate internet shorthand across generations.', '☎️', 'generation'],
  ['boomer_to_genz', 'Boomer → Gen Z', 'Give an old-school message current slang.', '📱', 'generation'],
  ['medieval_royal', 'Medieval Royal', 'Issue the message as a royal proclamation.', '👑', 'generation'],

  ['bunzli', 'Swiss Bünzli', 'Add rule-following Swiss-neighbor energy.', '🇨🇭', 'culture'],
  ['german_spiesser', 'German Spießer', 'Make it formally obsessed with order and rules.', '🇩🇪', 'culture'],
];

const FEATURED_EXAMPLES = ["I'm fine.", 'Do whatever you want.'];

const SURPRISE_EXAMPLES = [
  // Dating and relationships
  'Okay, have fun.',
  "I'm not mad.",
  'If you want to.',
  'Do whatever you want.',
  "It's fine.",
  'Nothing is wrong.',
  'You should already know.',
  "I don't care where we eat.",
  'Maybe another time.',
  "Don't worry about it.",
  'I guess.',
  'We need to talk.',
  "I'm just tired.",
  'Sure.',
  'Whatever.',
  "It's up to you.",

  // Friends and family
  'Call me when you have time.',
  'You never visit anymore.',
  "Don't buy me anything.",
  'Do what makes you happy.',
  'We were just talking about you.',
  'You seem busy lately.',
  "It's your life.",
  "I'm not judging.",
  'No, go ahead without me.',
  'It would be nice to hear from you sometimes.',

  // Work and clients
  "Let's circle back.",
  'Interesting idea.',
  "I'll take a look.",
  'Can you make this more strategic?',
  "Let's keep this lean.",
  'No rush, but today would be great.',
  'Per my last email.',
  "Let's discuss this offline.",
  'We should align before sharing this.',
  'This needs a bit more polish.',
  "I'm not sure this is the right priority.",
  'Can you own this?',
  'Thanks for the update.',
  'Noted.',

  // Cross-cultural and diplomatic language
  'We will consider it carefully.',
  'That may be difficult.',
  'Perhaps we can revisit this later.',
  'Your proposal is very ambitious.',
  'We should build more consensus.',
  'Please feel free to improve it.',
  "Let's see.",
  'We may need a little more time.',

  // Tech and startup language
  'Works on my machine.',
  "It's a small refactor.",
  'We can ship it behind a flag.',
  'The API is basically done.',
  'There is just one edge case.',
  "Let's not over-engineer this.",
  'We can fix it later.',
  "It's technically possible.",
];

const DEFAULT_PROFILE = {
  id: 'sarah',
  name: 'Sarah',
  relationship: 'dating',
  duration: '3 months',
  closeness: 'Knows me well',
  directness: 3,
  warmth: 5,
  formality: 1,
  detail: 4,
  personalityType: 'People-first introvert',
  personalityStyle: 'Warm and observant.',
  conflictStyle: 'Avoids direct conflict at first.',
  decisionStyle: 'Likes shared decisions but appreciates initiative.',
  textingBehavior: 'Usually warm and detailed.',
  cultureLanguage: 'English-speaking; prefers context over bluntness.',
  observations: "Sometimes says it's fine when she has a preference.",
  confirmedPatterns: [],
};

// New users start with one concrete, clearly fictional demo profile so the
// product is immediately understandable. Users can replace or edit it.
const STARTER_PROFILES = [DEFAULT_PROFILE];
const LEGACY_SEEDED_PROFILES = new Set(['sarah', 'jordan-boss', 'maya-coworker', 'alex-client', 'dad-family', 'leo-investor']);

function isUntouchedNeutralProfile(profile) {
  return profile?.id === 'primary-person'
    && (!profile.name || profile.name === 'Your person')
    && profile.relationship === 'custom'
    && !profile.duration
    && !profile.closeness
    && profile.directness === 3
    && profile.warmth === 3
    && profile.formality === 3
    && profile.detail === 3
    && !profile.personalityType
    && !profile.personalityStyle
    && !profile.conflictStyle
    && !profile.decisionStyle
    && !profile.textingBehavior
    && !profile.cultureLanguage
    && !profile.observations
    && !(profile.confirmedPatterns || []).length;
}

function normalizeEmail(value) {
  return typeof value === 'string' ? value.replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase() : '';
}

function plausibleEmail(value) {
  const email = normalizeEmail(value);
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function readJson(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null');
    return value ?? fallback;
  } catch { return fallback; }
}

function writeJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Storage can be unavailable. */ }
}

function prepareScreenshot(file) {
  return new Promise((resolve, reject) => {
    if (!file || !/^image\/(png|jpeg|webp)$/i.test(file.type)) return reject(new Error('Choose a PNG, JPEG, or WebP screenshot.'));
    if (file.size > 12_000_000) return reject(new Error('That screenshot is too large. Choose an image under 12 MB.'));
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that screenshot.'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('Could not read that screenshot.'));
      image.onload = () => {
        const maxSide = 1_500;
        const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve({ fileName: file.name, imageData: canvas.toDataURL('image/jpeg', 0.82) });
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function relationshipLabel(id) {
  return RELATIONSHIPS.find(([value]) => value === id)?.[1] || 'Custom';
}

function profileContextTier(score) {
  if (!Number.isFinite(score)) return 'PROFILE CONTEXT';
  if (score >= 70) return 'PROFILE-INFORMED';
  if (score >= 40) return 'SOME CONTEXT';
  return 'LOW CONTEXT';
}

function profileEvidenceLabels(profile) {
  const evidence = [];
  if (profile.relationship && profile.relationship !== 'custom') evidence.push('relationship');
  if (profile.observations) evidence.push('your note');
  if (profile.personalityStyle || profile.conflictStyle || profile.decisionStyle || profile.textingBehavior) evidence.push('communication details');
  if (profile.evidenceSources?.length || profile.aiEstimate) evidence.push('shared sample');
  if (profile.confirmedPatterns?.length) evidence.push('your feedback');
  return evidence;
}

function EmailGate({ onUnlock }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    const email = normalizeEmail(value);
    if (!plausibleEmail(email)) return setError('Enter a valid email address.');
    setBusy(true);
    setError('');
    try {
      const response = await axios.post(`${API}/submit-email`, { email });
      if (!response.data?.persisted) throw new Error('Email was not stored');
      localStorage.setItem(EMAIL_KEY, email);
      onUnlock(email);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Could not save your email. Try again.');
    } finally { setBusy(false); }
  };
  return (
    <main className="v3-gate">
      <form className="v3-gate-card" onSubmit={submit} noValidate>
        <span>VIBE TRANSLATOR V3</span>
        <h1>READ THE PERSON.<br />NOT JUST THE TEXT.</h1>
        <p>Enter your email. No password and no verification. Your chats are stored for this beta.</p>
        <label htmlFor="v3-email">EMAIL ADDRESS</label>
        <input id="v3-email" type="email" inputMode="email" autoComplete="email" value={value} onChange={(event) => { setValue(event.target.value); setError(''); }} placeholder="you@example.com" autoFocus />
        <div className="v3-form-error" role="alert">{error}</div>
        <button type="submit" disabled={busy}>{busy ? 'SAVING…' : 'ENTER V3'} <ArrowRight /></button>
      </form>
    </main>
  );
}

function ProductHeader({ onTour }) {
  return (
    <header className="v3-hero">
      <div>
        <h1>VIBE<br />TRANSLATOR</h1>
        <p>Make it <strong>funny.</strong> Decode the subtext.</p>
      </div>
      <div className="v3-stamp">
        <b>💀 FR FR 💀</b>
        <button type="button" onClick={onTour}><HelpCircle /> HOW IT WORKS</button>
      </div>
    </header>
  );
}

const NAV_ITEMS = [
  ['play', Sparkles, 'Play'],
  ['translate', Zap, 'Translate'],
  ['people', UsersRound, 'People'],
  ['me', CircleUserRound, 'Me'],
];

function ProductNav({ active, onChange }) {
  return (
    <nav className="v3-nav" aria-label="Primary">
      {NAV_ITEMS.map(([id, Icon, label]) => (
        <button type="button" key={id} className={active === id ? 'is-active' : ''} onClick={() => onChange(id)}>
          <Icon /> <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function PersonSummary({ profile, profiles, onSelect, onEdit, onAdd }) {
  const [open, setOpen] = useState(false);
  const summaryParts = [
    profile.relationship !== 'custom' ? relationshipLabel(profile.relationship) : '',
    profile.duration,
    profile.closeness,
  ].filter(Boolean);
  const summary = summaryParts.length
    ? summaryParts.join(' • ')
    : (profileEvidenceLabels(profile).length ? 'Context saved' : 'Learns from your feedback');
  return (
    <section className="v3-section v3-person-summary">
      <div className="v3-section-label">WHO SAID IT?</div>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span className="v3-person-copy">
          <strong>{profile.name || 'YOUR PERSON'}</strong>
          <small>{summary}</small>
        </span>
        {open ? <ChevronUp /> : <ChevronDown />}
      </button>
      {open && <div className="v3-person-picker">
        <strong>CHOOSE A PERSON</strong>
        <div>{profiles.map((item) => <button type="button" className={item.id === profile.id ? 'is-active' : ''} key={item.id} onClick={() => { onSelect(item.id); setOpen(false); }}><b>{item.name || 'YOUR PERSON'}</b><small>{relationshipLabel(item.relationship)}</small></button>)}</div>
        <footer><button type="button" onClick={onAdd}><Plus /> ADD ANOTHER PERSON</button><button type="button" onClick={onEdit}>EDIT {profile.name.toUpperCase()}'S PROFILE</button></footer>
      </div>}
    </section>
  );
}

function ProfileEditor({ email, profile, onChange, onClose }) {
  const [sourceMode, setSourceMode] = useState('screenshot');
  const [sourceText, setSourceText] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimateBusy, setEstimateBusy] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  const hasAdvancedData = Boolean(
    profile.aiEstimate || profile.evidenceSources?.length || profile.confirmedPatterns?.length,
  );
  const [showAdvanced, setShowAdvanced] = useState(() => hasAdvancedData);
  const field = (key, value) => onChange({ ...profile, [key]: value });

  useEffect(() => {
    setSourceText(''); setSourceUrl(''); setScreenshot(null); setEstimate(null); setEstimateError('');
    setShowAdvanced(hasAdvancedData);
  }, [profile.id, hasAdvancedData]);

  const chooseScreenshot = async (event) => {
    setEstimateError(''); setEstimate(null);
    try { setScreenshot(await prepareScreenshot(event.target.files?.[0])); }
    catch (error) { setScreenshot(null); setEstimateError(error.message); }
    event.target.value = '';
  };

  const requestEstimate = async () => {
    setEstimateBusy(true); setEstimateError(''); setEstimate(null);
    try {
      const response = await axios.post(`${API}/v3/profile-estimate`, {
        email,
        recipient: profile,
        sourceType: sourceMode,
        sourceText: sourceText.trim(),
        sourceUrl: sourceMode === 'linkedin' ? sourceUrl.trim() : '',
        fileName: screenshot?.fileName || '',
        imageData: sourceMode === 'screenshot' ? screenshot?.imageData || '' : '',
      });
      setEstimate(response.data.estimate);
    } catch (requestError) {
      setEstimateError(requestError.response?.data?.detail || 'Could not build that rough profile. Try again.');
    } finally { setEstimateBusy(false); }
  };

  const sourceRecord = () => ({
    id: `source-${Date.now()}`,
    type: sourceMode,
    label: sourceMode === 'screenshot' ? screenshot?.fileName || 'Screenshot' : sourceMode === 'linkedin' ? sourceUrl.trim() || 'LinkedIn text' : 'Pasted text',
    createdAt: new Date().toISOString(),
    roughEstimate: true,
  });

  const saveRoughEstimate = () => {
    if (!estimate) return;
    onChange({
      ...profile,
      aiEstimate: { ...estimate, sourceType: sourceMode, sourceUrl: sourceUrl.trim(), fileName: screenshot?.fileName || '', createdAt: new Date().toISOString() },
      evidenceSources: [sourceRecord(), ...(profile.evidenceSources || [])].slice(0, 12),
    });
    setEstimate(null);
  };

  const confirmEstimate = (value = estimate || profile.aiEstimate) => {
    if (!value) return;
    onChange({
      ...profile,
      ...value.traits,
      personalityStyle: value.personalityStyle,
      conflictStyle: value.conflictStyle,
      decisionStyle: value.decisionStyle,
      textingBehavior: value.textingBehavior,
      cultureLanguage: value.cultureLanguage,
      observations: [profile.observations, value.observations].filter(Boolean).join('\n').slice(0, 600),
      aiEstimate: null,
      evidenceSources: estimate ? [sourceRecord(), ...(profile.evidenceSources || [])].slice(0, 12) : profile.evidenceSources || [],
    });
    setEstimate(null);
  };

  const visibleEstimate = estimate || profile.aiEstimate;
  const canEstimate = sourceMode === 'screenshot' ? Boolean(screenshot) : Boolean(sourceText.trim());
  return (
    <section className="v3-profile-editor">
      <div className="v3-editor-heading"><div><span>QUICK PROFILE</span><h2>START IN SECONDS.</h2><p>A name is enough to begin. Add one useful note if you have it; Vibe learns from your feedback.</p></div>{onClose && <button type="button" onClick={onClose}>DONE</button>}</div>
      <section className="v3-quick-profile">
        <div className="v3-form-grid v3-quick-fields">
          <label>NAME<input value={profile.name} maxLength={60} onChange={(event) => field('name', event.target.value)} /></label>
          <label>RELATIONSHIP<select value={profile.relationship} onChange={(event) => field('relationship', event.target.value)}>{RELATIONSHIPS.map(([id, label]) => <option value={id} key={id}>{label}</option>)}</select></label>
        </div>
        <label className="v3-quick-note">OPTIONAL NOTE <small>Anything Vibe should know?</small><textarea value={profile.observations} maxLength={600} onChange={(event) => field('observations', event.target.value)} placeholder="e.g. Usually warm, but gets brief when annoyed." /></label>
        <button type="button" className="v3-advanced-toggle" onClick={() => setShowAdvanced((value) => !value)}>{showAdvanced ? 'HIDE ADVANCED PROFILE' : 'ADD MORE CONTEXT (OPTIONAL)'} <ChevronDown className={showAdvanced ? 'is-open' : ''} /></button>
      </section>
      {showAdvanced && <>
      <section className="v3-profile-builder">
        <div className="v3-builder-heading"><div><span>ASSISTED PROFILE</span><h3>GIVE VIBE SOMETHING REAL TO READ.</h3><p>AI proposes a rough communication model. You decide whether any of it becomes part of the profile.</p></div><WandSparkles /></div>
        <div className="v3-source-tabs" role="tablist" aria-label="Profile evidence type">
          <button type="button" className={sourceMode === 'screenshot' ? 'is-active' : ''} onClick={() => { setSourceMode('screenshot'); setEstimate(null); setEstimateError(''); }}><ImagePlus /> SCREENSHOT</button>
          <button type="button" className={sourceMode === 'linkedin' ? 'is-active' : ''} onClick={() => { setSourceMode('linkedin'); setEstimate(null); setEstimateError(''); }}><Linkedin /> LINKEDIN</button>
          <button type="button" className={sourceMode === 'text' ? 'is-active' : ''} onClick={() => { setSourceMode('text'); setEstimate(null); setEstimateError(''); }}><FileText /> PASTE TEXT</button>
        </div>
        <div className="v3-source-panel">
          {sourceMode === 'screenshot' && <><label className="v3-upload-box"><ImagePlus /><strong>{screenshot ? screenshot.fileName : 'CHOOSE A SCREENSHOT'}</strong><small>Messages, emails, social posts, or a profile screenshot. PNG, JPEG, or WebP.</small><input type="file" accept="image/png,image/jpeg,image/webp" onChange={chooseScreenshot} /></label><textarea value={sourceText} maxLength={8000} onChange={(event) => setSourceText(event.target.value)} placeholder="Optional context: what conversation is this, and what should Vibe pay attention to?" /></>}
          {sourceMode === 'linkedin' && <><div className="v3-estimate-warning"><AlertTriangle /><p><strong>VERY ROUGH ESTIMATE.</strong> LinkedIn is curated professional content. It can suggest work communication style—not the whole person or how they behave privately.</p></div><input type="url" value={sourceUrl} maxLength={500} onChange={(event) => setSourceUrl(event.target.value)} placeholder="https://www.linkedin.com/in/…" /><textarea value={sourceText} maxLength={8000} onChange={(event) => setSourceText(event.target.value)} placeholder="Paste their headline, About section, or a few posts. A URL alone usually cannot be read reliably." /></>}
          {sourceMode === 'text' && <textarea value={sourceText} maxLength={8000} onChange={(event) => setSourceText(event.target.value)} placeholder="Paste several messages, an email, captions, or any writing sample…" />}
          <button type="button" className="v3-build-estimate" onClick={requestEstimate} disabled={!canEstimate || estimateBusy}><WandSparkles /> {estimateBusy ? 'READING THE SIGNALS…' : 'BUILD A ROUGH ESTIMATE'}</button>
          {estimateError && <div className="v3-error" role="alert">{estimateError}</div>}
        </div>
        {visibleEstimate && <article className="v3-estimate-card">
          <header><div><span>ROUGH AI ESTIMATE</span><strong>Review before trusting it.</strong></div>{profile.aiEstimate && !estimate && <button type="button" aria-label="Remove rough estimate" onClick={() => onChange({ ...profile, aiEstimate: null })}><Trash2 /></button>}</header>
          <div className="v3-estimate-traits">{Object.entries(visibleEstimate.traits || {}).map(([key, value]) => <span key={key}><b>{key}</b>{value}/5</span>)}</div>
          <p>{visibleEstimate.personalityStyle}</p>
          <ul>{(visibleEstimate.signals || []).map((signal) => <li key={signal}>{signal}</li>)}</ul>
          <aside><AlertTriangle /> {(visibleEstimate.caveats || []).join(' ')}</aside>
          <footer>{estimate && <button type="button" onClick={saveRoughEstimate}>KEEP AS ROUGH ESTIMATE</button>}<button type="button" onClick={() => confirmEstimate()}>CONFIRM &amp; USE IN PROFILE</button></footer>
        </article>}
        {(profile.evidenceSources || []).length > 0 && <div className="v3-evidence-list"><strong>SOURCES ADDED</strong>{profile.evidenceSources.slice(0, 4).map((source) => <span key={source.id}>{source.type.toUpperCase()} · {source.label}</span>)}</div>}
      </section>
      <div className="v3-form-grid">
        <label>DURATION<input value={profile.duration} maxLength={80} onChange={(event) => field('duration', event.target.value)} placeholder="e.g. 3 months" /></label>
        <label>HOW WELL DO YOU KNOW THEM?<input value={profile.closeness} maxLength={80} onChange={(event) => field('closeness', event.target.value)} placeholder="e.g. Knows me well" /></label>
      </div>
      <div className="v3-traits">
        {[
          ['directness', 'Directness'], ['warmth', 'Warmth'], ['formality', 'Formality'], ['detail', 'Detail'],
        ].map(([key, label]) => (
          <label key={key}><span>{label}<b>{profile[key] ?? 3}/5</b></span><input type="range" min="1" max="5" value={profile[key] ?? 3} onChange={(event) => field(key, Number(event.target.value))} /></label>
        ))}
      </div>
      <div className="v3-form-stack">
        <label>PERSONALITY TYPE<input value={profile.personalityType || ''} maxLength={120} onChange={(event) => field('personalityType', event.target.value)} placeholder="Optional: e.g. ENFP, analytical introvert, people-first" /></label>
        <label>PERSONALITY STYLE<textarea value={profile.personalityStyle} maxLength={240} onChange={(event) => field('personalityStyle', event.target.value)} placeholder="How they generally think, react, and relate to people" /></label>
        <label>CONFLICT STYLE<textarea value={profile.conflictStyle} maxLength={240} onChange={(event) => field('conflictStyle', event.target.value)} placeholder="Direct, avoidant, needs time, wants resolution…" /></label>
        <label>DECISION-MAKING STYLE<textarea value={profile.decisionStyle} maxLength={240} onChange={(event) => field('decisionStyle', event.target.value)} placeholder="Fast, collaborative, analytical, preference-led…" /></label>
        <label>TYPICAL TEXTING BEHAVIOUR<textarea value={profile.textingBehavior} maxLength={300} onChange={(event) => field('textingBehavior', event.target.value)} placeholder="Length, emoji use, response timing, what changes under stress…" /></label>
        <label>CULTURAL / LANGUAGE CONTEXT<textarea value={profile.cultureLanguage} maxLength={240} onChange={(event) => field('cultureLanguage', event.target.value)} placeholder="Only context you know is relevant—never stereotypes" /></label>
      </div>
      <div className="v3-patterns">
        <strong>CONFIRMED PATTERNS LEARNED FROM FEEDBACK</strong>
        {(profile.confirmedPatterns || []).length ? (profile.confirmedPatterns || []).map((pattern, index) => <p key={`${pattern}-${index}`}>{pattern}</p>) : <p className="is-empty">None yet. Feedback after translations will teach this profile.</p>}
      </div>
      </>}
    </section>
  );
}

function TranslateView({ email, profile, profiles, onSelectProfile, onAddProfile, onProfileChange, onEditProfile, history, setHistory, saved, setSaved }) {
  const [message, setMessage] = useState("It's fine, do whatever you want.");
  const [surpriseIndex, setSurpriseIndex] = useState(() => Math.floor(Math.random() * SURPRISE_EXAMPLES.length));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [whyOpen, setWhyOpen] = useState(false);
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [copied, setCopied] = useState('');
  const [feedback, setFeedback] = useState('');
  const [correction, setCorrection] = useState('');

  const analyze = async (alternate = false) => {
    if (!message.trim()) return setError('Paste a message to decode.');
    setLoading(true); setError(''); setFeedback('');
    try {
      const response = await axios.post(`${API}/v3/analyze`, {
        email,
        message: message.trim(),
        recipient: profile,
        alternate,
        previousReading: alternate ? result?.reading : '',
      });
      setResult(response.data);
      const item = { id: response.data.requestId, at: new Date().toISOString(), type: 'translate', person: profile.name, message: message.trim(), result: response.data.reading, profileContextScore: response.data.profileContextScore };
      const next = [item, ...history.filter((entry) => entry.id !== item.id)].slice(0, 100);
      setHistory(next); writeJson(HISTORY_KEY, next);
      window.setTimeout(() => document.getElementById('v3-result')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 40);
    } catch (requestError) { setError(requestError.response?.data?.detail || 'Could not read that vibe. Try again.'); }
    finally { setLoading(false); }
  };

  const copyText = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(key); window.setTimeout(() => setCopied(''), 1200);
  };

  const submitFeedback = async (verdict, note = '') => {
    if (!result || ['accurate', 'inaccurate'].includes(feedback)) return;
    const learnedPattern = verdict === 'accurate'
      ? `Confirmed: ${result.reading}`.slice(0, 600)
      : `User correction: ${note}`.slice(0, 600);
    const confirmedPatterns = Array.isArray(profile.confirmedPatterns) ? profile.confirmedPatterns : [];
    const nextProfile = {
      ...profile,
      confirmedPatterns: [learnedPattern, ...confirmedPatterns.filter((item) => item !== learnedPattern)].slice(0, 8),
    };
    onProfileChange(nextProfile);
    try {
      await axios.post(`${API}/v3/feedback`, {
        email,
        requestId: result.requestId,
        profileId: profile.id,
        verdict,
        correction: note,
        learnedPattern,
        profileSnapshot: nextProfile,
      });
      setFeedback(verdict);
    } catch (requestError) { setError(requestError.response?.data?.detail || 'Feedback could not be saved.'); }
  };

  const isSaved = result && saved.some((item) => item.id === result.requestId);
  const toggleSave = () => {
    if (!result) return;
    const next = isSaved ? saved.filter((item) => item.id !== result.requestId) : [{ id: result.requestId, person: profile.name, message, reading: result.reading, replies: result.replyIdeas, at: new Date().toISOString() }, ...saved].slice(0, 100);
    setSaved(next); writeJson(SAVED_KEY, next);
  };

  const surpriseMe = () => {
    setMessage(SURPRISE_EXAMPLES[surpriseIndex]);
    // 17 is coprime with the 56-item library, so every prompt appears once
    // before the sequence repeats while still starting at a random point.
    setSurpriseIndex((current) => (current + 17) % SURPRISE_EXAMPLES.length);
    setError('');
  };

  return (
    <div className="v3-view v3-translate">
      <PersonSummary profile={profile} profiles={profiles} onSelect={onSelectProfile} onAdd={onAddProfile} onEdit={onEditProfile} />
      <section className="v3-section v3-message">
        <label htmlFor="v3-message">THE MESSAGE</label>
        <textarea id="v3-message" value={message} maxLength={400} onChange={(event) => { setMessage(event.target.value); setError(''); }} placeholder="Paste exactly what they sent…" />
        <span>{message.length}/400</span>
      </section>
      <button type="button" className="v3-cta" onClick={() => analyze(false)} disabled={loading}><Sparkles /> {loading ? 'READING THE ROOM…' : 'WHAT DID THEY MEAN?'}</button>
      {!result && !loading && <div className="v3-examples"><span>TRY EXAMPLES</span><div>{FEATURED_EXAMPLES.map((example) => <button type="button" key={example} onClick={() => setMessage(example)}>{example}</button>)}<button type="button" onClick={surpriseMe}>SURPRISE ME <Shuffle /></button></div></div>}
      {error && <div className="v3-error" role="alert">{error}</div>}
      {loading && <div className="v3-loading"><i /><i /><i /><span>Using {profile.name}'s profile—not a generic relationship stereotype.</span></div>}
      {result && !loading && (
        <section id="v3-result" className="v3-result" aria-live="polite">
          <article className="v3-read-card">
            <div className="v3-result-top"><strong><i /> {profileContextTier(result.profileContextScore)}</strong><button type="button" onClick={() => analyze(true)}>ANOTHER TAKE <RefreshCw /></button></div>
            <p className="v3-context-note">Using {profileEvidenceLabels(profile).join(', ') || 'the message alone'} for {profile.name || 'this person'}. This describes available evidence, not a prediction.</p>
            <p>{result.reading}</p>
            <button type="button" className="v3-why" onClick={() => setWhyOpen((value) => !value)}>WHY? {whyOpen ? <ChevronUp /> : <ChevronDown />}</button>
            {whyOpen && <div className="v3-reasons"><ul>{result.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>{result.alternateRead && <p><b>Also possible:</b> {result.alternateRead}</p>}</div>}
          </article>
          <h2 className="v3-subhead">WHAT SHOULD I SAY?</h2>
          <article className="v3-reply-card">
            <button type="button" className="v3-main-reply" onClick={() => copyText(result.replyIdeas[0].text, 'reply-0')}><MessageCircleMore /><span><small>{result.replyIdeas[0].label}</small>{result.replyIdeas[0].text}</span>{copied === 'reply-0' ? <Check /> : <Copy />}</button>
            <button type="button" className="v3-more-replies" onClick={() => setRepliesOpen((value) => !value)}>MORE REPLIES <ArrowRight /></button>
            {repliesOpen && <div className="v3-reply-options">{result.replyIdeas.slice(1).map((reply, index) => <button type="button" key={reply.label} onClick={() => copyText(reply.text, `reply-${index + 1}`)}><span><b>{reply.label}</b>{reply.text}</span>{copied === `reply-${index + 1}` ? <Check /> : <Copy />}</button>)}</div>}
          </article>
          <div className="v3-feedback">
            {feedback ? <strong><Check /> PROFILE UPDATED. FUTURE READS WILL USE THIS.</strong> : <><span>WAS THIS {profile.name.toUpperCase()}?</span><button type="button" onClick={() => submitFeedback('accurate')}><ThumbsUp /> NAILED IT</button><button type="button" onClick={() => setFeedback('correcting')}><ThumbsDown /> NOT REALLY</button></>}
            {feedback === 'correcting' && <form onSubmit={(event) => { event.preventDefault(); if (correction.trim()) { setFeedback(''); submitFeedback('inaccurate', correction.trim()); } }}><input value={correction} onChange={(event) => setCorrection(event.target.value)} placeholder={`What would ${profile.name} actually mean?`} /><button>SAVE CORRECTION</button></form>}
          </div>
          <button type="button" className="v3-save" onClick={toggleSave}><Bookmark fill={isSaved ? 'currentColor' : 'none'} /> {isSaved ? 'SAVED FOR LATER' : 'SAVE VIBE FOR LATER'} <span className={isSaved ? 'on' : ''}><i /></span></button>
        </section>
      )}
    </div>
  );
}

function PlayView({ email, history, setHistory }) {
  const [text, setText] = useState('We are almost ready to launch.');
  const [mode, setMode] = useState('founder_mode');
  const [category, setCategory] = useState('corporate');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const selected = PLAY_MODES.find(([id]) => id === mode);
  const visibleModes = PLAY_MODES.filter(([, , , , modeCategory]) => modeCategory === category);
  const chooseCategory = (nextCategory) => {
    setCategory(nextCategory);
    setMode(PLAY_MODES.find(([, , , , modeCategory]) => modeCategory === nextCategory)?.[0] || 'founder_mode');
    setOutput('');
    setError('');
  };
  const play = async () => {
    if (!text.trim()) return setError('Type something first.');
    setLoading(true); setError('');
    try {
      const response = await axios.post(`${API}/translate`, { email, text: text.trim().slice(0, 200), mode });
      setOutput(response.data.translated);
      const item = { id: response.data.requestId, at: new Date().toISOString(), type: 'play', mode: response.data.mode, message: text.trim(), result: response.data.translated };
      const next = [item, ...history.filter((entry) => entry.id !== item.id)].slice(0, 100);
      setHistory(next); writeJson(HISTORY_KEY, next);
    } catch (requestError) { setError(requestError.response?.data?.detail || 'Play mode failed. Try again.'); }
    finally { setLoading(false); }
  };
  return (
    <div className="v3-view v3-play">
      <div className="v3-page-intro"><span>PLAY MODE</span><h2>MAKE IT SCREENSHOT-WORTHY.</h2><p>Pick a tile, type anything, and share the result.</p></div>
      <section className="v3-play-input"><textarea value={text} maxLength={200} onChange={(event) => setText(event.target.value)} /><button type="button" onClick={play} disabled={loading}><Sparkles /> {loading ? 'COOKING…' : `PLAY ${selected?.[1] || ''}`}</button></section>
      {error && <div className="v3-error">{error}</div>}
      {output && <article className="v3-play-output"><span>{selected?.[3]} {selected?.[1]}</span><p>{output}</p><button type="button" onClick={() => navigator.clipboard.writeText(output)}><Copy /> COPY</button></article>}
      <div className="v3-mode-heading"><strong>{PLAY_MODES.length} MODES</strong><span>Choose a category, then tap a tile.</span></div>
      <div className="v3-mode-categories">{PLAY_CATEGORIES.map(([id, name, emoji]) => <button type="button" key={id} className={category === id ? 'is-active' : ''} aria-pressed={category === id} onClick={() => chooseCategory(id)}><b>{emoji}</b>{name}<span>{PLAY_MODES.filter(([, , , , itemCategory]) => itemCategory === id).length}</span></button>)}</div>
      <div className="v3-mode-grid">{visibleModes.map(([id, name, description, emoji]) => <button type="button" key={id} className={mode === id ? 'is-active' : ''} aria-pressed={mode === id} aria-label={`${name}. ${description}`} title={description} onClick={() => setMode(id)}><b>{emoji}</b><strong>{name}</strong><span aria-hidden="true">{description}</span></button>)}</div>
    </div>
  );
}

function PeopleView({ email, profiles, currentId, onSelect, onUpdate, onAdd }) {
  const current = profiles.find((profile) => profile.id === currentId) || profiles[0];
  return (
    <div className="v3-view v3-people">
      <div className="v3-page-intro"><span>PEOPLE</span><h2>THE PERSON IS THE CONTEXT.</h2><p>Start with a name. Add only the notes and feedback that actually help.</p></div>
      <div className="v3-people-list">{profiles.map((profile) => <button type="button" key={profile.id} className={profile.id === current.id ? 'is-active' : ''} onClick={() => onSelect(profile.id)}><b>{profile.name || 'YOUR PERSON'}</b><small>{relationshipLabel(profile.relationship)}</small></button>)}<button type="button" className="v3-add-person" onClick={onAdd}><Plus /><b>ADD PERSON</b></button></div>
      <ProfileEditor email={email} profile={current} onChange={onUpdate} />
    </div>
  );
}

function AddPersonDialog({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const submit = (event) => {
    event.preventDefault();
    onCreate({ name: name.trim() });
  };
  return <div className="v3-dialog-backdrop" role="presentation"><form className="v3-add-dialog" role="dialog" aria-modal="true" aria-labelledby="v3-add-title" onSubmit={submit}>
    <span>NEW PERSON</span><h2 id="v3-add-title">ONE THING. DONE.</h2><p>Give them a name or label. You can add the relationship and any useful context later, after you get your first read.</p>
    <label>NAME OR LABEL <small>OPTIONAL</small><input value={name} onChange={(event) => setName(event.target.value)} maxLength={60} placeholder="e.g. Sarah, my manager, or housemate" autoFocus /></label>
    <div><button type="button" onClick={onClose}>CANCEL</button><button type="submit">CREATE PROFILE <ArrowRight /></button></div>
  </form></div>;
}

function MeView({ email, history, saved, profiles, onChangeEmail, onTour }) {
  return (
    <div className="v3-view v3-me">
      <div className="v3-page-intro"><span>ME</span><h2>YOUR VIBE MEMORY.</h2><p>Stats and history live together here. Your person profiles stay on this device.</p></div>
      <div className="v3-stat-grid"><article><b>{history.length}</b><span>TOTAL TRANSLATIONS</span></article><article><b>{profiles.length}</b><span>PEOPLE PROFILES</span></article><article><b>{saved.length}</b><span>SAVED VIBES</span></article><article><b>{history.filter((item) => item.type === 'play').length}</b><span>PLAY MODE RUNS</span></article></div>
      <section className="v3-history"><div className="v3-history-head"><span><History /> HISTORY</span><small>Stored on this device</small></div>{history.length ? history.slice(0, 20).map((item) => <article key={item.id}><span>{item.type === 'play' ? item.mode : item.person}</span><time>{new Date(item.at).toLocaleDateString()}</time><strong>{item.message}</strong><p>{item.result}</p></article>) : <div className="v3-empty">Your translations will appear here.</div>}</section>
      <section className="v3-account"><strong>{email}</strong><button type="button" onClick={onTour}>REPLAY HOW IT WORKS</button><button type="button" onClick={onChangeEmail}>CHANGE EMAIL</button></section>
    </div>
  );
}

function VibeV3Product({ email, onChangeEmail }) {
  const [profiles, setProfiles] = useState(() => {
    const stored = readJson(PROFILE_KEY, []);
    const existing = Array.isArray(stored) ? stored : [];
    // Remove only untouched profiles that came from the old demo roster. A
    // profile with feedback, imported evidence, or a renamed person remains
    // user data and is preserved.
    const userProfiles = existing.filter((item) => {
      if (isUntouchedNeutralProfile(item)) return false;
      if (!LEGACY_SEEDED_PROFILES.has(item?.id)) return true;
      return Boolean(item?.confirmedPatterns?.length || item?.evidenceSources?.length || item?.aiEstimate || (item?.name && !['Sarah', 'Jordan', 'Maya', 'Alex', 'Dad', 'Leo'].includes(item.name)));
    });
    const seeded = userProfiles.length ? userProfiles : STARTER_PROFILES;
    try { localStorage.setItem(PROFILE_SEED_KEY, '1'); } catch { /* Storage can be unavailable. */ }
    return seeded;
  });
  const [currentId, setCurrentId] = useState(() => profiles[0]?.id || DEFAULT_PROFILE.id);
  const [active, setActive] = useState('play');
  const [profileOpen, setProfileOpen] = useState(false);
  const [history, setHistory] = useState(() => readJson(HISTORY_KEY, []));
  const [saved, setSaved] = useState(() => readJson(SAVED_KEY, []));
  // Play is the first-run hook. Keep the longer explainer available from
  // “How it works” without covering the product before someone can try it.
  const [showTour, setShowTour] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const profile = profiles.find((item) => item.id === currentId) || profiles[0];

  useEffect(() => { writeJson(PROFILE_KEY, profiles); }, [profiles]);
  const updateProfile = (next) => setProfiles((items) => items.map((item) => item.id === next.id ? next : item));
  const addPerson = (setup = {}) => {
    const id = `person-${Date.now()}`;
    const next = {
      ...DEFAULT_PROFILE,
      id,
      name: setup.name || 'New person',
      duration: '',
      closeness: '',
      relationship: setup.relationship || 'custom',
      // Blank until the user supplies a note/sample or moves a slider. A
      // neutral-looking 3/5 is not evidence and should not steer the model.
      directness: null,
      warmth: null,
      formality: null,
      detail: null,
      personalityType: setup.personalityType || '',
      personalityStyle: '',
      conflictStyle: '',
      decisionStyle: '',
      textingBehavior: '',
      cultureLanguage: '',
      observations: '',
      confirmedPatterns: [],
      evidenceSources: [],
      aiEstimate: null,
    };
    setProfiles((items) => [...items, next]); setCurrentId(id);
    return id;
  };
  const closeTour = () => { try { sessionStorage.setItem(TOUR_KEY, '1'); } catch {} setShowTour(false); };

  return (
    <main className="v3-shell">
      <ProductHeader onTour={() => setShowTour(true)} />
      <ProductNav active={active} onChange={(next) => { setActive(next); setProfileOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
      <div className="v3-content">
        {active === 'translate' && <>
          {profileOpen ? <ProfileEditor email={email} profile={profile} onChange={updateProfile} onClose={() => setProfileOpen(false)} /> : <TranslateView email={email} profile={profile} profiles={profiles} onSelectProfile={setCurrentId} onAddProfile={() => setShowAddPerson(true)} onProfileChange={updateProfile} onEditProfile={() => setProfileOpen(true)} history={history} setHistory={setHistory} saved={saved} setSaved={setSaved} />}
        </>}
        {active === 'play' && <PlayView email={email} history={history} setHistory={setHistory} />}
        {active === 'people' && <PeopleView email={email} profiles={profiles} currentId={currentId} onSelect={setCurrentId} onUpdate={updateProfile} onAdd={() => setShowAddPerson(true)} />}
        {active === 'me' && <MeView email={email} history={history} saved={saved} profiles={profiles} onChangeEmail={onChangeEmail} onTour={() => setShowTour(true)} />}
      </div>
      <footer className="v3-footer">NO CAP, FR FR — BUILT FOR THE MEMES, MADE FOR REAL LIFE. <span>V3</span></footer>
      {showTour && <VibeStoryTour onClose={closeTour} />}
      {showAddPerson && <AddPersonDialog onClose={() => setShowAddPerson(false)} onCreate={(setup) => { addPerson(setup); setShowAddPerson(false); setActive('translate'); }} />}
    </main>
  );
}

export default function VibeV3() {
  const [email, setEmail] = useState(() => {
    const preview = ['localhost', '127.0.0.1'].includes(window.location.hostname) && new URLSearchParams(window.location.search).get('preview') === '1';
    if (preview) return 'preview@local.invalid';
    try { const stored = localStorage.getItem(EMAIL_KEY) || ''; return plausibleEmail(stored) ? normalizeEmail(stored) : ''; } catch { return ''; }
  });
  if (!email) return <EmailGate onUnlock={setEmail} />;
  return <VibeV3Product email={email} onChangeEmail={() => { localStorage.removeItem(EMAIL_KEY); setEmail(''); }} />;
}
